import type {
  IHeatmapFilterPayload,
  IHeatmapBaseFilterPayload,
  IHeatmapDraftBaseFilterPayload,
  EHeatmapDraftBaseFilterType,
  EHeatmapBaseFilterType,
  EHeatmapFilterType,
} from '../types/filter';

import { DEFAULT_VIEW_ID } from '@gem-sdk/heatmap-react';
import { create } from 'zustand';

interface IHeatmapFilterState {
  /** Base filters per viewId (applied filters used for API queries) */
  base: Map<string, IHeatmapBaseFilterPayload>;
  /** Draft base filters per viewId (pending changes before apply) */
  draftBase: Map<string, IHeatmapDraftBaseFilterPayload>;
  /** Active filters per viewId (applied filters used for API queries) */
  data: Map<string, IHeatmapFilterPayload>;
  /** Draft filters per viewId (pending changes before apply) */
  draftData: Map<string, IHeatmapFilterPayload>;
}

interface IHeatmapFilterActions {
  setBaseFilter: <K extends EHeatmapBaseFilterType>(
    filterType: K,
    value: IHeatmapBaseFilterPayload[K],
    viewId?: string,
  ) => void;
  setBaseFilters: (payload: Partial<IHeatmapBaseFilterPayload>, viewId?: string) => void;
  setDraftBaseFilter: <K extends EHeatmapDraftBaseFilterType>(
    filterType: K,
    value: IHeatmapDraftBaseFilterPayload[K],
    viewId?: string,
  ) => void;
  setDraftBaseFilters: (payload: Partial<IHeatmapDraftBaseFilterPayload>, viewId?: string) => void;
  clearDraftBaseFilters: (viewId?: string) => void;
  initializeBaseFilters: (payload: Partial<IHeatmapBaseFilterPayload>, viewId?: string) => void;
  setFilter: <K extends EHeatmapFilterType>(
    filterType: K,
    value: IHeatmapFilterPayload[K],
    viewId?: string,
  ) => void;
  setDraftFilter: <K extends EHeatmapFilterType>(
    filterType: K,
    value: IHeatmapFilterPayload[K],
    viewId?: string,
  ) => void;
  setDraftFilters: (payload: Partial<IHeatmapFilterPayload>, viewId?: string) => void;
  removeFilter: (filterType: EHeatmapFilterType, viewId?: string) => void;
  applyFilters: (viewId?: string) => void;
  applyBaseFilters: (viewId?: string) => void;
  cancelFilters: (viewId?: string) => void;
  resetFilters: (viewId?: string) => void;
  copyView: (fromViewId: string, toViewId: string) => void;
  clearView: (viewId: string) => void;
  resetAll: () => void;
  initializeFilters: (payload: Partial<IHeatmapFilterPayload>, viewId?: string) => void;
}

export interface IHeatmapFilterStore extends IHeatmapFilterState, IHeatmapFilterActions {}

const initialState: IHeatmapFilterState = {
  data: new Map(),
  base: new Map(),
  draftBase: new Map(),
  draftData: new Map(),
};

export const useHeatmapFilterStore = create<IHeatmapFilterStore>()((set, get) => ({
  ...initialState,

  setBaseFilter: (filterType, value, viewId = DEFAULT_VIEW_ID) => {
    const currentBase = get().base.get(viewId) ?? {};
    const newBase = new Map(get().base);
    newBase.set(viewId, { ...currentBase, [filterType]: value });
    set({ base: newBase });
  },

  setBaseFilters: (payload, viewId = DEFAULT_VIEW_ID) => {
    const currentBase = get().base.get(viewId) ?? {};
    const newBase = new Map(get().base);
    newBase.set(viewId, { ...currentBase, ...payload });
    set({ base: newBase });
  },

  setDraftBaseFilter: (filterType, value, viewId = DEFAULT_VIEW_ID) => {
    const currentDraftBase = get().draftBase.get(viewId) ?? {};
    const newDraftBase = new Map(get().draftBase);
    newDraftBase.set(viewId, { ...currentDraftBase, [filterType]: value });
    set({ draftBase: newDraftBase });
  },

  setDraftBaseFilters: (payload, viewId = DEFAULT_VIEW_ID) => {
    const currentDraft = get().draftBase.get(viewId) ?? {};
    const newDraft = new Map(get().draftBase);
    newDraft.set(viewId, { ...currentDraft, ...payload });
    set({ draftBase: newDraft });
  },

  clearDraftBaseFilters: (viewId = DEFAULT_VIEW_ID) => {
    const newDraftBase = new Map(get().draftBase);
    newDraftBase.delete(viewId);
    set({ draftBase: newDraftBase });
  },

  setFilter: (filterType, value, viewId = DEFAULT_VIEW_ID) => {
    const currentDraft = get().draftData.get(viewId) ?? {};
    const currentData = get().data.get(viewId) ?? {};
    const newDraft = new Map(get().draftData);
    const newData = new Map(get().data);
    newDraft.set(viewId, { ...currentDraft, [filterType]: value });
    newData.set(viewId, { ...currentData, [filterType]: value });
    set({ draftData: newDraft, data: newData });
  },

  setDraftFilter: (filterType, value, viewId = DEFAULT_VIEW_ID) => {
    const currentDraft = get().draftData.get(viewId) ?? {};
    const newDraft = new Map(get().draftData);
    newDraft.set(viewId, { ...currentDraft, [filterType]: value });
    set({ draftData: newDraft });
  },

  setDraftFilters: (payload, viewId = DEFAULT_VIEW_ID) => {
    const currentDraft = get().draftData.get(viewId) ?? {};
    const newDraft = new Map(get().draftData);
    newDraft.set(viewId, { ...currentDraft, ...payload });
    set({ draftData: newDraft });
  },

  removeFilter: (filterType, viewId = DEFAULT_VIEW_ID) => {
    const currentDraft = get().draftData.get(viewId);
    if (!currentDraft) return;

    const newDraft = new Map(get().draftData);
    const { [filterType]: _, ...rest } = currentDraft;
    newDraft.set(viewId, rest as IHeatmapFilterPayload);
    set({ draftData: newDraft });
  },

  applyFilters: (viewId = DEFAULT_VIEW_ID) => {
    const draft = get().draftData.get(viewId);
    if (!draft) return;

    const newData = new Map(get().data);
    newData.set(viewId, { ...draft });
    set({ data: newData });
  },

  applyBaseFilters: (viewId = DEFAULT_VIEW_ID) => {
    const draftBase = get().draftBase.get(viewId);
    if (!draftBase) return;

    const currentBase = get().base.get(viewId) ?? {};
    const newBase = new Map(get().base);
    newBase.set(viewId, { ...currentBase, ...draftBase });
    set({ base: newBase });
  },

  cancelFilters: (viewId = DEFAULT_VIEW_ID) => {
    const active = get().data.get(viewId) ?? {};
    const newDraft = new Map(get().draftData);
    newDraft.set(viewId, { ...active });

    const newDraftBase = new Map(get().draftBase);
    newDraftBase.delete(viewId);

    set({ draftData: newDraft, draftBase: newDraftBase });
  },

  resetFilters: (viewId = DEFAULT_VIEW_ID) => {
    const newData = new Map(get().data);
    const newDraft = new Map(get().draftData);

    newData.set(viewId, {});
    newDraft.set(viewId, {});

    set({ data: newData, draftData: newDraft });
  },

  copyView: (fromViewId, toViewId) =>
    set((prev) => {
      const newData = new Map(prev.data);
      const newBase = new Map(prev.base);

      newData.set(toViewId, prev.data.get(fromViewId) ?? {});
      newBase.set(toViewId, prev.base.get(fromViewId) ?? {});

      return { data: newData, draftData: newData, base: newBase };
    }),

  clearView: (viewId) =>
    set((prev) => {
      const newData = new Map(prev.data);
      const newDraft = new Map(prev.draftData);
      const newBase = new Map(prev.base);
      const newDraftBase = new Map(prev.draftBase);

      newData.delete(viewId);
      newDraft.delete(viewId);
      newBase.delete(viewId);
      newDraftBase.delete(viewId);

      return { data: newData, draftData: newDraft, base: newBase, draftBase: newDraftBase };
    }),

  resetAll: () =>
    set({ data: new Map(), draftData: new Map(), base: new Map(), draftBase: new Map() }),

  initializeFilters: (payload, viewId = DEFAULT_VIEW_ID) =>
    set((prev) => {
      const newData = new Map(prev.data);
      const newDraft = new Map(prev.draftData);
      newData.set(viewId, { ...payload });
      newDraft.set(viewId, { ...payload });
      return { data: newData, draftData: newDraft };
    }),

  initializeBaseFilters: (payload, viewId = DEFAULT_VIEW_ID) => {
    const newBase = new Map(get().base);
    newBase.set(viewId, { ...payload });
    set({ base: newBase });
  },
}));
